<template>
  <v-dialog
    v-model="display"
    :width="dialogWidth"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        v-bind="textFieldProps"
        v-on="on"
        dense
        :disabled="disabled"
        outlined
        prepend-inner-icon="mdi-calendar"
        readonly
        :value="datetimeString"
        :label="label"
      >
        <template v-slot:progress>
          <slot name="progress">
            <v-progress-linear
              color="primary"
              indeterminate
              absolute
              height="2"
            ></v-progress-linear>
          </slot>
        </template>
      </v-text-field>
    </template>

    <v-card>
      <v-card-text class="px-0 py-0">
        <v-tabs
          fixed-tabs
          v-model="activeTab"
        >
          <v-tab key="calendar">
            <slot name="dateIcon">
              <v-icon>mdi-calendar</v-icon>
            </slot>
          </v-tab>
          <v-tab key="timer">
            <slot name="timeIcon">
              <v-icon>mdi-clock-outline</v-icon>
            </slot>
          </v-tab>
          <v-tab-item key="calendar">
            <v-date-picker
              v-model="dateString"
              v-bind="propsDatePicker"
              @input="onDateChange"
              full-width
            ></v-date-picker>
          </v-tab-item>
          <v-tab-item key="timer">
            <v-time-picker
              v-model="timeString"
              v-bind="propsTimePicker"
              format="24hr"
              ref="timer"
              full-width
            ></v-time-picker>
          </v-tab-item>
        </v-tabs>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <slot
          name="actions"
          :parent="this"
        >
          <v-btn
            color="primary"
            text
            @click.native="onCancelClick"
            >{{ $t("commons.clear") }}
          </v-btn>
          <v-btn
            color="success"
            text
            @click="onSaveClick"
            >{{ $t("commons.ok") }}
          </v-btn>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  isSameDay,
  stringToDate,
  toDatetime,
  toISODate,
  toISODatetime,
  toTime
} from "@/utils/datetime"

const DEFAULT_DIALOG_WIDTH = 340

export default {
  name: "DatetimePicker",
  props: {
    value: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean
    },
    dialogWidth: {
      type: Number,
      default: DEFAULT_DIALOG_WIDTH
    },
    textFieldProps: {
      type: Object
    },
    max: {
      type: Object
    },
    min: {
      type: Object
    },
    label: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      activeTab: 0,
      dateString: "",
      display: false,
      isoDatetimeString: "",
      timeString: ""
    }
  },
  computed: {
    datetime() {
      return stringToDate(`${this.dateString} ${this.timeString || "00:00"}:00`)
    },
    datetimeString() {
      return toDatetime(this.value)
    },
    hasSelectedDate() {
      return !!this.dateString
    },
    propsDatePicker() {
      let props = {}
      if (this.max) {
        props = { ...props, max: toISODate(this.max) }
      }
      if (this.min) {
        props = { ...props, min: toISODate(this.min) }
      }
      return props
    },
    propsTimePicker() {
      let props = {}
      if (this.max && this.hasSelectedDate && isSameDay(this.datetime, this.max)) {
        props = { ...props, max: toTime(this.max) }
      }
      if (this.min && this.hasSelectedDate && isSameDay(this.datetime, this.min)) {
        props = { ...props, min: toTime(this.min) }
      }
      return props
    }
  },
  methods: {
    onCancelClick() {
      this.reset()
    },
    onDateChange(newValue) {
      this.activeTab = 1
    },
    onSaveClick() {
      if (this.hasSelectedDate) {
        this.$emit("input", this.datetime)
      }
      this.reset()
    },
    reset() {
      this.display = false
      this.activeTab = 0
      if (this.$refs.timer) {
        this.$refs.timer.selectingHour = true
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.isoDatetimeString = toISODatetime(newValue)
          this.dateString = this.isoDatetimeString.slice(0, 10)
          this.timeString = this.isoDatetimeString.slice(11, 16)
        }
      }
    }
  }
}
</script>
